import _ from "lodash";
import React, { useCallback, useState, useEffect, useRef, memo } from "react";
import classNames from "classnames";
import {
  ChevronLeftOutlined,
  ChevronRightOutlined,
  PageSquareOutlined,
} from "@toddle-edu/ds-icons";
import { IconButton } from "@toddle-edu/ds-web";
import StudentEndSidebarItem from "./StudentEndSidebarItem.js";
import classes from "./StudentEndSidebar.scss";
import StudentEndSidebarProgress from "./StudentEndSidebarProgress";
import {
  isMobileByUserAgent,
  isTabletByUserAgent,
  useWindowDimensions,
} from "Utils";
import { useI18n } from "Hooks";

const StudentEndSidebarItemWrapper = memo(
  ({
    mode,
    option,
    collapsedState,
    activeQuestion,
    instructionsActive,
    updateActiveQuestion,
  }) => {
    return (
      <StudentEndSidebarItem
        mode={mode}
        collapsedState={collapsedState}
        activeQuestion={activeQuestion}
        updateActiveQuestion={updateActiveQuestion}
        instructionsActive={instructionsActive}
        {...option}
      />
    );
  }
);

StudentEndSidebarItemWrapper.displayName = "StudentEndSidebarItemWrapper";

/**
 * Renders the student end sidebar.
 * @returns {JSX.Element}
 */
const StudentEndSidebar = props => {
  const {
    options,
    questionCount,
    attemptedCount,
    worksheetInstructions,
    updateActiveQuestion,
    activeQuestion,
    onInstructionsClick,
    mode,
  } = props;
  const { t } = useI18n(["worksheet"]);
  useWindowDimensions();
  const isMobile = isMobileByUserAgent();
  const isTabletSize = isTabletByUserAgent();
  const isDesktopSize = !isMobile && !isTabletSize;
  const [collapsedState, setCollapsedState] = useState(true);
  const sidebarRef = useRef(null);
  const progressWrapRef = useRef(null);
  const [triggerPos, setTriggerPos] = useState({
    top: 0,
    left: 0,
    ready: false,
  });
  const [activeKey, setActiveKey] = useState(null); // "INSTRUCTIONS" or questionId

  useEffect(() => {
    if (isDesktopSize) setCollapsedState(false);
    else setCollapsedState(true);
  }, [isDesktopSize]);

  useEffect(() => {
    if (activeKey === "INSTRUCTIONS" && activeQuestion) {
      setActiveKey(null);
    }
  }, [activeQuestion, activeKey]);

  const handleToggleCollapse = useCallback(() => {
    if (isTabletSize && !isDesktopSize) {
      setCollapsedState(prev => !prev);
    }
  }, [isTabletSize, isDesktopSize]);

  const updateTriggerPosition = useCallback(() => {
    if (!(isTabletSize && !isDesktopSize)) {
      if (triggerPos.ready) setTriggerPos({ top: 0, left: 0, ready: false });
      return;
    }
    const sidebarEl = sidebarRef.current;
    const progressEl = progressWrapRef.current;
    if (!sidebarEl || !progressEl) return;
    const sidebarRect = sidebarEl.getBoundingClientRect();
    const progressRect = progressEl.getBoundingClientRect();
    const top = progressRect.top + progressRect.height / 2;
    const left = sidebarRect.left;
    setTriggerPos({ top, left, ready: true });
  }, [isTabletSize, isDesktopSize, triggerPos.ready]);

  useEffect(() => {
    updateTriggerPosition();
  }, [updateTriggerPosition, collapsedState, attemptedCount, questionCount]);

  useEffect(() => {
    const onScroll = () => updateTriggerPosition();
    const onResize = () => updateTriggerPosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [updateTriggerPosition]);

  const handleSidebarInstructionsClick = () => {
    setActiveKey("INSTRUCTIONS");
    onInstructionsClick();
  };

  const handleSidebarInstructionsEnter = e => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSidebarInstructionsClick();
    }
  };

  const instructionsClasses = classNames(classes.instructions, {
    active: activeKey === "INSTRUCTIONS",
  });

  if (isMobile) return null;

  return (
    <aside
      ref={sidebarRef}
      className={classNames(classes.sidebarRoot, {
        [classes.sidebarCollapsed]:
          collapsedState && isTabletSize && !isDesktopSize,
      })}
      aria-label="Worksheet navigation sidebar"
    >
      {isTabletSize && !isDesktopSize && triggerPos.ready ? (
        <div
          className={classes.collapseTrigger}
          style={{
            position: "fixed",
            top: triggerPos.top,
            left: triggerPos.left,
            transform: "translate(calc(-50% - 4px), -50%)",
            zIndex: 999,
          }}
        >
          <IconButton
            className={classes.collapsableTriggerButton}
            dsVersion="2.0"
            icon={
              collapsedState ? (
                <ChevronLeftOutlined />
              ) : (
                <ChevronRightOutlined />
              )
            }
            variant="neutral"
            type="outlined"
            size="small"
            onClick={handleToggleCollapse}
            aria-label={collapsedState ? "Expand sidebar" : "Collapse sidebar"}
          />
        </div>
      ) : null}
      {/* TOP BAR */}
      <div className={classes.topBar}>
        <div className={classes.progressWrap} ref={progressWrapRef}>
          <StudentEndSidebarProgress
            value={attemptedCount}
            total={questionCount}
          />
          <span className={classes.progressText}>
            {collapsedState
              ? `${attemptedCount}/${questionCount}`
              : `${t("worksheet:questions_attempted_with_count", {
                  count: attemptedCount,
                  totalCount: questionCount,
                })}`}
          </span>
        </div>
      </div>
      {!_.isEmpty(worksheetInstructions) && (
        <div
          className={instructionsClasses}
          role="button"
          tabIndex={0}
          onClick={handleSidebarInstructionsClick}
          onKeyDown={handleSidebarInstructionsEnter}
          aria-label={t("worksheet:worksheet_instructions")}
        >
          <PageSquareOutlined size="xx-small" variant="subtle" />
          <span>{t("classRoom:instructions")}</span>
        </div>
      )}
      <div className={classes.sections} aria-label="Sections">
        {_.map(options, option => (
          <StudentEndSidebarItemWrapper
            key={_.get(option, "id") || _.get(option, "resourceId")}
            option={option}
            collapsedState={collapsedState}
            activeQuestion={activeQuestion}
            updateActiveQuestion={updateActiveQuestion}
            instructionsActive={activeKey === "INSTRUCTIONS"}
            mode={mode}
          />
        ))}
      </div>
    </aside>
  );
};

export default StudentEndSidebar;
